import { CreativeOSFeatures, CreativeOSPages } from '@/_shared/subscriptionPlans'
import AccessControl from '@/components/AccessControl'
import { MasonryComponent, TypedResponsiveMasonry } from '@/components/ResponsiveMasonryWrapper.tsx'
import { Stack, Text } from '@/components/custom-components'
import { Loader } from '@/components/custom-components/Loader'
import NotFoundBox from '@/components/custom-components/NotFound'
import { Button } from '@/components/shadcn/button'
import { StartFreeTrialAdCard } from '@/components/templates/AdGridView'
import { LandingAdCard } from '@/components/templates/LandingAdCard'
import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions'
import useUserPermissions from '@/hooks/useUserPermissions'
import { showTemplateRecentButton } from '@/utils/data/feeds.ts'
import { PRO_PLAN } from '@/utils/data/plans'
import { trpc } from '@/utils/trpc'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

export const Route = createFileRoute('/share/templates/emails/$adID')({
  component: SharedEmailTemplate,
})

function SharedEmailTemplate() {
  const navigate = useNavigate({ from: '/share/templates/emails/$adID' })
  const { adID } = Route.useParams()
  const { cacheBuster } = Route.useSearch()

  const { data: authUser, isLoading: isLoadingMe } = trpc.me.useQuery(null)
  const { squareRef, columns, gutterWidth } = useCardLayoutWithImpressions()

  const {
    data: emailTemplates,
    isLoading: isLoadingEmailTemplate,
    error: emailTemplateError,
  } = trpc.getSharedEmailTemplate.useQuery({ emailTemplateId: parseInt(adID) })

  const { currentPlanConfigs, permissions: permissionData } = useUserPermissions()

  if (!cacheBuster) {
    navigate({
      search: (old) => {
        return {
          ...old,
          cacheBuster: Math.random(),
        }
      },
      params: (old) => {
        return {
          ...old,
        }
      },
    })
  }

  return (
    <div className={'relative h-[97vh]'}>
      <Stack className="!mb-6 gap-8">
        <Stack className="gap-3">
          <div className={'flex justify-between lg:justify-start gap-5 items-center'}>
            <Text weight="semibold" size={'xxl'}>
              Email Templates
            </Text>
            {!authUser ? (
              <div className="flex gap-2 items-center">
                <a target={'_blank'} href={'https://app.creativeos.io/login'}>
                  <Button size={'sm'} variant={'ghost'} className="flex">
                    Log In
                  </Button>
                </a>

                <a target={'_blank'} href={'https://www.creativeos.io/#sign'}>
                  <Button size={'sm'} variant={'destructivePurple'} className="flex">
                    Get Started
                  </Button>
                </a>
              </div>
            ) : authUser &&
              !(
                permissionData?.usersPlans.includes(PRO_PLAN.plan_id) ||
                permissionData?.usersPlans.includes(PRO_PLAN.no_trial_plan_id)
              ) ? (
              <AccessControl
                feature={CreativeOSFeatures.LOCKED}
                FeatureLocked={
                  <Button variant={'destructivePurple'} size={'sm'}>
                    Upgrade
                  </Button>
                }
              />
            ) : (
              <></>
            )}
          </div>
          <Text className="text-thememutedforeground w-fit" size={'base'} weight={'normal'}>
            Somebody shared an email template with you!
            {!authUser
              ? ` Sign up for CreativeOS Pro Plan to access this and our full library of Ad, Email, and Landing Page Templates, and so much more `
              : !(
                    permissionData?.usersPlans.includes(PRO_PLAN.plan_id) ||
                    permissionData?.usersPlans.includes(PRO_PLAN.no_trial_plan_id)
                  )
                ? ' Upgrade to CreativeOS Pro to access our full library of Email and Landing Page Templates.'
                : ''}
            {!authUser && (
              <a className="text-black underline" target={'_blank'} href="https://www.creativeos.io/">
                Learn more about CreativeOS.
              </a>
            )}
          </Text>
        </Stack>

        {isLoadingEmailTemplate || isLoadingMe ? (
          <div className={'flex justify-center items-center w-full h-screen m-auto'}>
            <Loader />
          </div>
        ) : emailTemplateError ? (
          <NotFoundBox />
        ) : (
          emailTemplates && (
            <div ref={squareRef} className={'relative w-full pb-10'}>
              <div>
                <TypedResponsiveMasonry
                  columnsCountBreakPoints={columns ? { 0: columns } : {}} // Columns is determined by the width of the container
                >
                  <MasonryComponent gutter={gutterWidth / 16 + 'rem'}>
                    {[
                      emailTemplates[0] && (
                        <AccessControl
                          feature={
                            [
                              ...currentPlanConfigs
                                .map((config) =>
                                  config.accessiblePages?.[CreativeOSPages.EMAIL_TEMPLATES]?.limit == 'infinity'
                                    ? 20
                                    : config.accessiblePages?.[CreativeOSPages.EMAIL_TEMPLATES]?.limit,
                                )
                                .filter((limit) => limit !== undefined)
                                .sort((a, b) => (b as number) - (a as number)),
                              20,
                            ][0] > 0
                              ? undefined
                              : CreativeOSFeatures.LOCKED
                          }
                          FeatureUnlocked={
                            <LandingAdCard
                              IsShared
                              key={emailTemplates[0]['Email ID']}
                              adData={{
                                Screenshot: emailTemplates[0]['Email Screenshot'],
                                ID: emailTemplates[0]['Email ID'].toString(),
                                created: emailTemplates[0].Created,
                                category: emailTemplates[0].Category,
                                isSaved: emailTemplates[0].isSaved,
                                imageUrl: emailTemplates[0].imageUrl,
                                canEdit: emailTemplates[0].canEdit,
                                editorFileId: emailTemplates[0].editorFileId,
                              }}
                              showWaterMark={!authUser}
                              openDetailsOnInit={authUser ? true : false}
                              type={'email'}
                            />
                          }
                          FeatureLocked={
                            <LandingAdCard
                              IsShared
                              IsLocked
                              key={emailTemplates[0]['Email ID']}
                              adData={{
                                Screenshot: emailTemplates[0]['Email Screenshot'],
                                ID: emailTemplates[0]['Email ID'].toString(),
                                created: emailTemplates[0].Created,
                                category: emailTemplates[0].Category,
                                isSaved: emailTemplates[0].isSaved,
                                imageUrl: emailTemplates[0].imageUrl,
                                canEdit: emailTemplates[0].canEdit,
                                editorFileId: emailTemplates[0].editorFileId,
                              }}
                              showWaterMark={true}
                              type={'email'}
                            />
                          }
                        />
                      ),
                      ...(!authUser
                        ? [
                            <AccessControl
                              feature={CreativeOSFeatures.LOCKED}
                              FeatureLocked={
                                <StartFreeTrialAdCard
                                  key="startFreeTrial"
                                  fullWidthButton
                                  titleText={!authUser ? undefined : 'Upgrade to Unlock'}
                                  bodyText={!authUser ? undefined : 'Level up your ad workflow with CreativeOS Pro'}
                                  buttonText={!authUser ? 'Start Free Trial' : 'Upgrade Now'}
                                  href={!authUser ? 'https://www.creativeos.io/#sign' : undefined}
                                />
                              }
                            />,
                          ]
                        : []),
                      ...emailTemplates
                        .slice(
                          1,
                          Math.max(
                            [
                              ...currentPlanConfigs
                                .map((config) =>
                                  config.accessiblePages?.[CreativeOSPages.EMAIL_TEMPLATES]?.limit == 'infinity'
                                    ? 20
                                    : config.accessiblePages?.[CreativeOSPages.EMAIL_TEMPLATES]?.limit,
                                )
                                .filter((limit) => limit !== undefined)
                                .sort((a, b) => (b as number) - (a as number)),
                              20,
                            ][0],
                            -1,
                            1,
                          ),
                        )
                        .map((emailTemplate) => (
                          <LandingAdCard
                            key={emailTemplate['Email ID']}
                            showWaterMark={!authUser}
                            adData={{
                              Screenshot: emailTemplate['Email Screenshot'],
                              ID: emailTemplate['Email ID'].toString(),
                              created: emailTemplate.Created,
                              category: emailTemplate.Category,
                              isSaved: emailTemplate.isSaved,
                              imageUrl: emailTemplate.imageUrl,
                              canEdit: emailTemplate.canEdit,
                              editorFileId: emailTemplate.editorFileId,
                            }}
                            showRecentButton={showTemplateRecentButton(emailTemplate.Created)}
                            type={'email'}
                          />
                        )),
                      ...emailTemplates
                        .slice(
                          Math.max(
                            [
                              ...currentPlanConfigs
                                .map((config) =>
                                  config.accessiblePages?.[CreativeOSPages.EMAIL_TEMPLATES]?.limit == 'infinity'
                                    ? 20
                                    : config.accessiblePages?.[CreativeOSPages.EMAIL_TEMPLATES]?.limit,
                                )
                                .filter((limit) => limit !== undefined)
                                .sort((a, b) => (b as number) - (a as number)),
                              20,
                            ][0],

                            -1,
                            1,
                          ),
                          emailTemplates.length - 1,
                        )
                        .map((emailTemplate) => (
                          <LandingAdCard
                            key={emailTemplate['Email ID']}
                            showWaterMark={!authUser}
                            adData={{
                              Screenshot: emailTemplate['Email Screenshot'],
                              ID: emailTemplate['Email ID'].toString(),
                              created: emailTemplate.Created,
                              category: emailTemplate.Category,
                              isSaved: emailTemplate.isSaved,
                              imageUrl: emailTemplate.imageUrl,
                              canEdit: emailTemplate.canEdit,
                              editorFileId: emailTemplate.editorFileId,
                            }}
                            showRecentButton={showTemplateRecentButton(emailTemplate.Created)}
                            IsLocked
                            type={'email'}
                          />
                        )),
                    ]}
                  </MasonryComponent>
                </TypedResponsiveMasonry>
              </div>
            </div>
          )
        )}
      </Stack>
    </div>
  )
}
